import {
  DayOfWeek,
  IsoFields,
  LocalDate,
  TemporalAdjusters,
} from "@js-joda/core";
import { PeriodDayPicker } from "components/designV2/periodNavigator/periodDayPicker";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

export interface PeriodNavigatorProps {
  readonly allowNext?: boolean;
  readonly allowPrev?: boolean;
  readonly currentDate: LocalDate;
  readonly mode: "month" | "year" | "week" | "day";
  readonly nextAriaLabel?: string;
  readonly prevAriaLabel?: string;
  readonly showSubLabel?: boolean;
  readonly showYear?: boolean;
  readonly showYearInMonthSubLabel?: boolean;
  readonly onChange: (nextDate: LocalDate) => void;
}

const DAY_LOCALE = "sv-SE";

const getSupportedDateLocale = (language: string): "en-GB" | "sv-SE" =>
  language.startsWith("sv") ? "sv-SE" : "en-GB";

/** Navigation header for day/week/month/year period selection. */
export const PeriodNavigator = ({
  currentDate,
  mode,
  onChange,
  showSubLabel = false,
  showYear = true,
  showYearInMonthSubLabel = false,
  allowPrev = true,
  allowNext = true,
  prevAriaLabel = "Previous period",
  nextAriaLabel = "Next period",
}: PeriodNavigatorProps): JSX.Element => {
  const { i18n, t } = useTranslation();

  const toJsDate = (date: LocalDate): Date =>
    new Date(date.year(), date.monthValue() - 1, date.dayOfMonth());
  const capitalizeFirst = (value: string): string =>
    value.length > 0 ? value[0].toUpperCase() + value.slice(1) : value;

  const locale = useMemo(
    () => getSupportedDateLocale(i18n.language),
    [i18n.language],
  );
  const monthFormatter = useMemo(
    () => new Intl.DateTimeFormat(locale, { month: "long" }),
    [locale],
  );
  const dayMonthFormatter = useMemo(
    () => new Intl.DateTimeFormat(DAY_LOCALE, { month: "long" }),
    [],
  );
  const dayWeekdayFormatter = useMemo(
    () => new Intl.DateTimeFormat(DAY_LOCALE, { weekday: "short" }),
    [],
  );

  const getDayMonthName = (date: LocalDate): string => {
    return capitalizeFirst(dayMonthFormatter.format(toJsDate(date)));
  };

  const getDayWeekdayShort = (date: LocalDate): string => {
    const raw = dayWeekdayFormatter.format(toJsDate(date));
    const normalized = raw.endsWith(".") ? raw.slice(0, -1) : raw;
    return capitalizeFirst(normalized);
  };

  const formatSwedishOrdinal = (dayOfMonth: number): string => {
    const lastTwoDigits = dayOfMonth % 100;
    const lastDigit = dayOfMonth % 10;
    const suffix =
      (lastDigit === 1 && lastTwoDigits !== 11) ||
      (lastDigit === 2 && lastTwoDigits !== 12)
        ? ":a"
        : ":e";
    return `${dayOfMonth}${suffix}`;
  };

  const monthName = useMemo(() => {
    return capitalizeFirst(
      monthFormatter.format(toJsDate(currentDate.withDayOfMonth(1))),
    );
  }, [currentDate, monthFormatter]);
  const dayMonthName = getDayMonthName(currentDate);
  const dayWeekday = getDayWeekdayShort(currentDate);

  const weekStart = useMemo(
    () => currentDate.with(TemporalAdjusters.previousOrSame(DayOfWeek.MONDAY)),
    [currentDate],
  );
  const weekEnd = useMemo(() => weekStart.plusDays(6), [weekStart]);
  const weekNumber = useMemo(
    () => currentDate.get(IsoFields.WEEK_OF_WEEK_BASED_YEAR),
    [currentDate],
  );

  const label = useMemo(() => {
    if (mode === "year") return `${currentDate.year()}`;
    if (mode === "week") return `${t("Schedule.Week")} ${weekNumber}`;
    if (mode === "day") {
      return `${dayWeekday} ${formatSwedishOrdinal(currentDate.dayOfMonth())}`;
    }
    return `${monthName}${showYear ? ` ${currentDate.year()}` : ""}`;
  }, [currentDate, dayWeekday, mode, monthName, showYear, t, weekNumber]);

  const subLabel = useMemo(() => {
    if (!showSubLabel) return undefined;
    if (mode === "month") {
      const yearPart = showYearInMonthSubLabel ? ` ${currentDate.year()}` : "";
      return `1 - ${currentDate.lengthOfMonth()} ${monthName}${yearPart}`;
    }
    if (mode === "week") {
      const startMonthDate = toJsDate(weekStart.withDayOfMonth(1));
      const endMonthDate = toJsDate(weekEnd.withDayOfMonth(1));
      const startMonth = capitalizeFirst(monthFormatter.format(startMonthDate));
      const endMonth = capitalizeFirst(monthFormatter.format(endMonthDate));
      if (weekStart.monthValue() === weekEnd.monthValue()) {
        return `${weekStart.dayOfMonth()} - ${weekEnd.dayOfMonth()} ${startMonth} ${weekEnd.year()}`;
      }
      return `${weekStart.dayOfMonth()} ${startMonth} - ${weekEnd.dayOfMonth()} ${endMonth} ${weekEnd.year()}`;
    }
    if (mode === "day") {
      return `${dayMonthName} ${currentDate.year()}`;
    }
    return undefined;
  }, [
    currentDate,
    dayMonthName,
    mode,
    monthName,
    monthFormatter,
    showSubLabel,
    showYearInMonthSubLabel,
    weekEnd,
    weekStart,
  ]);

  const prevDate =
    mode === "year"
      ? currentDate.minusYears(1)
      : mode === "day"
        ? currentDate.minusDays(1)
        : mode === "week"
          ? currentDate.minusWeeks(1)
          : currentDate.minusMonths(1);

  const nextDate =
    mode === "year"
      ? currentDate.plusYears(1)
      : mode === "day"
        ? currentDate.plusDays(1)
        : mode === "week"
          ? currentDate.plusWeeks(1)
          : currentDate.plusMonths(1);

  const handlePrev = (): void => {
    if (!allowPrev) return;
    onChange(prevDate);
  };

  const handleNext = (): void => {
    if (!allowNext) return;
    onChange(nextDate);
  };

  return (
    <Container>
      <NavButton
        type="button"
        aria-label={prevAriaLabel}
        onClick={handlePrev}
        disabled={!allowPrev}
      >
        <CustomIcon name="v2/left-chevron" size="24px" color={ColorV2.Blue2} />
      </NavButton>
      <TextBlock>
        {mode === "day" ? (
          <PeriodDayPicker
            currentDate={currentDate}
            label={label}
            onChange={onChange}
            openAriaLabel={t("DatePicker.Open")}
          />
        ) : (
          <Label>{label}</Label>
        )}
        {subLabel && <SubLabel>{subLabel}</SubLabel>}
      </TextBlock>
      <NavButton
        type="button"
        aria-label={nextAriaLabel}
        onClick={handleNext}
        disabled={!allowNext}
      >
        <CustomIcon name="v2/right-chevron" size="24px" color={ColorV2.Blue2} />
      </NavButton>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-shrink: 0;
  column-gap: 16px;
  padding: 12px 20px;
  border-radius: 999px;
`;

const NavButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${ColorV2.Blue2};

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const TextBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  text-align: center;
`;

const Label = styled.div`
  ${TypographyV2.h3};
  color: ${ColorV2.Blue1};
  white-space: nowrap;
`;

const SubLabel = styled.div`
  ${TypographyV2.s1};
  color: ${ColorV2.Blue4};
`;
